/**
 * useContentMonetization Hook
 *
 * Owns local state, derived values and handlers for the Monetization step.
 * Mirrors Flutter's ContentMonetizationScreenState business logic from
 * lib/presentation/screens/publish/content_monetization_screen.dart.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { selectWalletCoins } from '@/presentation/store/slices/wallet/walletSlice';
import { loadBoostsUseCase } from '@/domain/usecases/monetization/loadBoostsUseCase';
import { MediaUtils } from '@/domain/utils/mediaUtils';
import { MathUtils } from '@/domain/utils/mathUtils';
import { TextInputFormatters } from '@/presentation/constants/formatters/textInputFormatters';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import type { BoostEntity } from '@/domain/entities/boost/boostEntity';
import type { MonetizationResult } from '@/domain/entities/publish/monetizationResult';

export type { MonetizationResult };

interface UseContentMonetizationParams {
  durationSeconds: number;
  screenKey?: string;
}

const getSelectedOption = (boost: BoostEntity) =>
  boost.options.find((o) => o.isSelected) ?? boost.options[0];

export const useContentMonetization = ({ durationSeconds, screenKey }: UseContentMonetizationParams) => {
  const { l10n } = useLocale();
  const walletCoins = useSelector(selectWalletCoins);

  const [boosts, setBoosts] = useState<BoostEntity[]>(() => loadBoostsUseCase.execute());
  const [allowPremiumContentPayment, setAllowPremiumContentPayment] = useState<boolean>(false);
  const [isBoosted, setIsBoosted] = useState<boolean>(true);
  const [allowUnlock, setAllowUnlock] = useState<boolean>(true);
  const [unlockPriceInputText, setUnlockPriceInputText] = useState<string>(
    () => MathUtils.getWalletAmount(remoteConfigService.contentUnlockDefaultDollars)
  );
  const [unlockPriceInputFocused, setUnlockPriceInputFocused] = useState<boolean>(false);

  // Derived from unlockPriceInputText: a number when the text is a complete,
  // in-range decimal; null while the user is mid-typing ("", ".", "5.", etc.).
  const unlockChargeUSD = useMemo<number | null>(() => {
    const valid = TextInputFormatters.isValidDecimalAmount(
      unlockPriceInputText,
      remoteConfigService.contentUnlockMinDollars,
      remoteConfigService.contentUnlockMaxDollars,
    );
    if (!valid) return null;
    return parseFloat(unlockPriceInputText.replace(',', '.'));
  }, [unlockPriceInputText]);

  const unlockPriceCoins = useMemo(
    () => MathUtils.coinsFromUSD(unlockChargeUSD ?? remoteConfigService.contentUnlockDefaultDollars),
    [unlockChargeUSD]
  );

  const isFreeContent = useMemo(
    () => MediaUtils.isFreeContent(durationSeconds),
    [durationSeconds]
  );

  const premiumVideoPriceCoins = useMemo(
    () => MediaUtils.getPremiumContentCoins(durationSeconds),
    [durationSeconds]
  );

  const selectedBoost = useMemo(
    () => boosts.find((b) => b.isSelected) ?? boosts[0],
    [boosts]
  );

  const boostPriceCoins = useMemo(() => {
    if (!isBoosted || !selectedBoost) return 0;
    return MathUtils.coinsFromUSD(getSelectedOption(selectedBoost).price);
  }, [isBoosted, selectedBoost]);

  // Wallet check tracks the actual cost the creator will be charged on submit:
  // boost is always charged (when enabled), but premium-video coins are only
  // charged when allowPremiumContentPayment is on. Matches Flutter parity:
  // _shouldRecharge() ignores _allowPremiumContentPayment because for premium
  // content the user is forced to enable that toggle (canSave gate) anyway.
  const shouldRecharge = useMemo(
    () => premiumVideoPriceCoins + boostPriceCoins > walletCoins,
    [premiumVideoPriceCoins, boostPriceCoins, walletCoins]
  );

  const rechargeAmount = useMemo(() => {
    const totalForWallet = premiumVideoPriceCoins + boostPriceCoins;
    return shouldRecharge ? totalForWallet - walletCoins : 0;
  }, [shouldRecharge, premiumVideoPriceCoins, boostPriceCoins, walletCoins]);

  const canSave = useMemo(
    () =>
      !shouldRecharge &&
      (isFreeContent || allowPremiumContentPayment) &&
      !unlockPriceInputFocused &&
      (!allowUnlock || unlockChargeUSD != null),
    [shouldRecharge, isFreeContent, allowPremiumContentPayment, unlockPriceInputFocused, allowUnlock, unlockChargeUSD]
  );

  const togglePremium = useCallback((value: boolean) => {
    setAllowPremiumContentPayment(value);
  }, []);

  const toggleBoosted = useCallback((value: boolean) => {
    setIsBoosted(value);
  }, []);

  const selectBoost = useCallback((id: string) => {
    setBoosts((prev) =>
      prev.map((b) => ({ ...b, isSelected: b.id === id }))
    );
  }, []);

  const cycleBoostOption = useCallback((boostId: string, forward: boolean) => {
    setBoosts((prev) =>
      prev.map((b) => {
        if (b.id !== boostId) return { ...b, isSelected: false };
        const currentIndex = Math.max(0, b.options.findIndex((o) => o.isSelected));
        const len = b.options.length;
        const nextIndex = forward
          ? (currentIndex + 1) % len
          : (currentIndex - 1 + len) % len;
        const options = b.options.map((o, i) => ({ ...o, isSelected: i === nextIndex }));
        return { ...b, isSelected: true, options };
      })
    );
  }, []);

  const toggleAllowUnlock = useCallback((value: boolean) => {
    setAllowUnlock(value);
  }, []);

  const setUnlockInputFocus = useCallback((focused: boolean) => {
    setUnlockPriceInputFocused(focused);
  }, []);

  const onUnlockPriceChange = useCallback((rawText: string) => {
    setUnlockPriceInputText((prev) =>
      TextInputFormatters.decimalAmount(
        rawText,
        prev,
        remoteConfigService.contentUnlockMinDollars,
        remoteConfigService.contentUnlockMaxDollars,
      ),
    );
  }, []);

  // TODO: Maange when Payment implemented on the web
  const handleRechargeWallet = useCallback(() => {
    InfoSheet.show({
      title: l10n.content.rechargeWallet,
      infoText: l10n.content.rechargeNotAvailable,
      screenKey,
    });
  }, [l10n, screenKey]);

  const buildResult = useCallback((): MonetizationResult => {
    const selectedOption = isBoosted && selectedBoost ? getSelectedOption(selectedBoost) : null;
    return {
      allowPremiumContentPayment,
      isBoosted,
      boostOptionId: selectedOption?.id ?? null,
      allowUnlock,
      unlockCharge: unlockChargeUSD != null ? MathUtils.coinsFromUSD(unlockChargeUSD) : null,
    };
  }, [allowPremiumContentPayment, isBoosted, selectedBoost, allowUnlock, unlockChargeUSD]);

  return {
    // state
    allowPremiumContentPayment,
    isBoosted,
    boosts,
    selectedBoost,
    allowUnlock,
    unlockChargeUSD,
    unlockPriceCoins,
    unlockPriceInputText,

    // derived
    isFreeContent,
    premiumVideoPriceCoins,
    boostPriceCoins,
    shouldRecharge,
    rechargeAmount,
    canSave,

    // handlers
    togglePremium,
    toggleBoosted,
    selectBoost,
    cycleBoostOption,
    toggleAllowUnlock,
    onUnlockPriceChange,
    setUnlockInputFocus,
    handleRechargeWallet,
    buildResult,
  };
};
